// Imports ------------------------------------------------------------------->
import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";

import {Dialog} from "../flipp-dialog/Dialog";
import {PageContext} from "../flipp-page/Page";
import {SearchBox} from "../flipp-searchbox/SearchBox";
import {AccountTriggerElement} from "../flipp-account-trigger/AccountTriggerElement";
import {ShoppingListTriggerElement} from "../flipp-shopping-list-trigger/ShoppingListTriggerElement";

import {inject} from "../../services/di";
import {AnalyticsService} from "../../services/AnalyticsService";
import {ConfigService, RuntimeConfig} from "../../services/ConfigService";
import {UserAgentService} from "../../services/UserAgentService";
import {StorageService} from "../../services/StorageService";
import {HistoryService} from "../../services/HistoryService";

import {AutocompleteContext} from "../../definitions/CommonDefinitions";
import {AuthenticationService} from "../../services/AuthenticationService";
import {EventService} from "../../services/EventService";
import {AuthStatus} from "../../services/definitions/IAuthenticationService";
import {LinkService} from "../../services/LinkService";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export interface ApplicationTopBarProps extends JSX.HTMLProps {
	search?: string;
	context?: PageContext;
}

@element("flipp-application-topbar")
export class ApplicationTopBar extends Component<ApplicationTopBarProps, {}> {
	@attr search: string;
	@attr context: PageContext;

	@inject userAgent: UserAgentService;
	@inject storage: StorageService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;
	@inject history: HistoryService;
	@inject auth: AuthenticationService;
	@inject eventService: EventService;
	@inject linkService: LinkService;

	refs: {
		companyMenu: Dialog;
		anchor: HTMLDivElement;
	};

	onAttach() {
		window.addEventListener("unsubscribe-from-redirect", this.unsubscribeFromRedirect);
	}

	onDetach() {
		window.removeEventListener("unsubscribe-from-redirect", this.unsubscribeFromRedirect);
		window.removeEventListener("redirect-to-form", this.redirectToForm);
	}

	render() {
		if (!this.config.showAppTopBar) {
			return null;
		}
		const appLink = this.userAgent.getAppLinkHref("Application Top Bar");
		/* For distribution we need to ensure the Help link is https. This is because if the
			* parent page is loaded in https, browsers will block the transition as it's "insecure".
			* Our freshdesk URL is cname'd to the flipp.com domain which doesn't use https. So for
			* distribution, use the full Freshdesk URL with https until we can have a proper certificate
			* on the cname'd version
			* */
		const helpLink = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
			"https://flipp.freshdesk.com/support/home" :
			i18n("help_link");

		const suppressedAppTopBar = !this.config.suppressedAppTopBar;

		return <template>
			<header flex-noshrink flex-horizontal style={{backgroundColor: this.config.getThemeColor()}}>
				<content />
				<div class="suppressed-app-top-bar" style={{backgroundColor: this.config.getThemeColor()}}>
				{suppressedAppTopBar ? <SearchBox search={this.search} autocompletecontext={AutocompleteContext.GLOBAL_SEARCH} flex-grow flex-horizontal flex-noshrink /> : null}

				<div ref="anchor" flex-horizontal anchor-point="right bottom" >
					<div class="links" flex-horizontal flex-noshrink>
						<a href="https://www.flippappinions.com/c/a/6kiwEIx1Ce287lz8RyHYr8" target="blank">
							<button class="settings">
								<img src="https://f.wishabi.net/creative/Distribution/images/icons/myMessageSvg.svg" width="44px" height="22px" alt=""/>
								<span>{i18n("give_feedback")}</span></button>
						</a>
					</div>
					<div class="mobile_feedback" flex-horizontal flex-noshrink>
						<a class="link_left_border" href="https://www.flippappinions.com/c/a/6kiwEIx1Ce287lz8RyHYr8" target="blank">
							<button class="settings">
								<img src="https://f.wishabi.net/creative/Distribution/images/icons/myMessageSvg.svg" width="44px" height="22px" alt=""/>
							</button>
						</a>
					</div>
					<div class="links" flex-horizontal flex-noshrink>
						<button class="settings"
										onClick={this.handleCompanyMenuClicked}>
							<span>{i18n("about_flipp")}</span></button>
					</div>
					<button class="ellipsis"
									anchor-point="right bottom"
									flex-noshrink
									onClick={this.handleCompanyMenuClicked}>
						{icons.verticalEllipsis}
					</button>
				</div>
				{suppressedAppTopBar ? <AccountTriggerElement /> : null}
				{suppressedAppTopBar && !this.userAgent.mobile ?
					<ShoppingListTriggerElement selected={this.context === "shoppingList"}/>
				:
					null
				}

				<Dialog class="settings-dialog"
								ref="companyMenu"
								anchor-point="right top"
								popover>
					<div class="arrow"></div>
					<hr/>
					<a target="_blank" href="https://corp.flipp.com">{i18n("company")}</a>
					<a target="_blank" href="https://corp.flipp.com/about">{i18n("about_us")}</a>
					<a target="_blank" href="https://corp.flipp.com/careers">{i18n("careers")}</a>
					<a target="_blank" href={appLink} onClick={(e: Event) => { this.handleAppLinkClick_(e); }}>{i18n("mobile_app")}</a>
					<a target="_blank" href={helpLink}>{i18n("help")}</a>
					<hr/>
					<a target="_blank" href="https://corp.flipp.com/flipp/terms_of_use">{i18n("terms_of_use")}</a>
					<a target="_blank" href="https://corp.flipp.com/flipp/privacy">{i18n("privacy_policy")}</a>
					<button class="data-button" onClick={this.handleFormClicked}>{i18n("my_data")}</button>
					<a target="_blank" href="https://corp.flipp.com/patents">{i18n("patents")}</a>
					<a target="_blank" href="https://corp.flipp.com/privacy#INFO55">{i18n("ad_choices")}</a>
					<hr/>
				</Dialog>
				</div>
			</header>
		</template>;
	}

	private handleAppLinkClick_ = (e: Event) => {
		if (this.userAgent.android || this.userAgent.iOS) {
			this.analytics.trackMobileInstall(this.userAgent.android ? "android" : "ios");
		}
	}

	private handleCompanyMenuClicked = (e: Event) => {
		this.refs.companyMenu.showModal(this.refs.anchor);
	}

	private handleFormClicked = () => {
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			this.redirectToForm();
		} else {
			this.eventService.showModalInForm();
			window.addEventListener("redirect-to-form", this.redirectToForm);
		}
	}

	private unsubscribeFromRedirect = () => {
		window.removeEventListener("redirect-to-form", this.redirectToForm);
	}

	private redirectToForm = () => {
		this.linkService.execute("/data", this);
	}
}
